/**
 * TypeScript types for the RPN Calculator
 */

export type TrigMode = 'deg' | 'rad' | 'grad';
export type DisplayMode = 'normal' | 'fix' | 'sci' | 'eng';
export type ArithmeticMode = 'none' | 'plus' | 'minus' | 'multiply' | 'divide';

export interface MemoryRegisters {
  r0: number;
  r1: number;
  r2: number;
  r3: number;
  r4: number;
  r5: number;
  r6: number;
  r7: number;
  r8: number;
  r9: number;
}

export interface StatisticsItem {
  valX: number;
  valY: number;
}

export interface ConversionConfig {
  fromfactor: number | string;
  tofactor: number | string;
  property: string;
  propertyindex: number;
  fromvalue: number | string;
  tovalue: number | string;
}

export interface CalculatorState {
  operationDone: boolean;
  enterPressed: boolean;
  cmode: TrigMode;
  displaymode: DisplayMode;
  mode_f: boolean;
  mode_g: boolean;
  mode_hyp: boolean;
  displayBuffer: string;
  lastx: number;
  stopressed: boolean;
  rclpressed: boolean;
  stoarithmetic: ArithmeticMode;
  rclarithmetic: ArithmeticMode;
  fixpressed: boolean;
  engpressed: boolean;
  scipressed: boolean;
  displayprecision: number;
  displayStack: boolean;
  audiofeedback: boolean;
  commaType: CommaType;
  leaveStackInfo: boolean;
}

export type CommaType = '1' | '2' | '3' | '4';

export interface StorageData {
  stack?: number[];
  memoryregisters?: MemoryRegisters;
  statisticsregisters?: StatisticsItem[];
  displaystack?: boolean;
  displayprecision?: number;
  displaymode?: DisplayMode;
  cmode?: TrigMode;
  audiofeedback?: boolean;
  commatype?: CommaType;
  conversionconfig?: ConversionConfig;
  _version?: string;
}

export type KeyStroke =
  | 'zero' | 'one' | 'two' | 'three' | 'four' | 'five' | 'six' | 'seven' | 'eight' | 'nine'
  | 'dot' | 'enter' | 'plus' | 'minus' | 'multiply' | 'divide'
  | 'sqrt' | 'xsquare' | 'onedivx' | 'chs' | 'eex' | 'backspace'
  | 'sin' | 'cos' | 'tan' | 'ex' | 'tenx' | 'pi'
  | 'sto' | 'rcl' | 'swapxy' | 'roll' | 'undo'
  | 'f' | 'g' | 'xfac' | 'percent' | 'conv' | 'sigmaplus'
  | 'refresh';

export const KEY_TO_DIGIT: Record<string, number | string> = {
  'zero': 0,
  'one': 1,
  'two': 2,
  'three': 3,
  'four': 4,
  'five': 5,
  'six': 6,
  'seven': 7,
  'eight': 8,
  'nine': 9,
  'dot': '.'
};

// Physical keyboard mapping
export const PHYSICAL_KEY_MAP: Record<string, KeyStroke> = {
  '0': 'zero',
  '1': 'one',
  '2': 'two',
  '3': 'three',
  '4': 'four',
  '5': 'five',
  '6': 'six',
  '7': 'seven',
  '8': 'eight',
  '9': 'nine',
  '.': 'dot',
  ',': 'dot',
  'Enter': 'enter',
  '+': 'plus',
  '-': 'minus',
  '*': 'multiply',
  '/': 'divide',
  'x': 'multiply',
  'Backspace': 'backspace',
  'Delete': 'backspace',
  'f': 'f',
  'F': 'f',
  'g': 'g',
  'G': 'g',
  's': 'sin',
  'S': 'sin',
  'c': 'cos',
  'C': 'cos',
  't': 'tan',
  'T': 'tan',
  'p': 'pi',
  'P': 'pi',
  'r': 'roll',
  'R': 'roll',
  'u': 'undo',
  'U': 'undo',
  'e': 'eex',
  'E': 'eex',
  '^': 'xsquare',
  '!': 'xfac',
  '%': 'percent',
  'Escape': 'backspace'
};
